<template>
  <div>
    <div class="d-flex align-center">
      <div>
        <div class="text-subtitle-1">文例</div>
        <div class="text-caption text-grey-darken-1">
          レイアウトの項目ごとに文例を登録します。エディタでセルを選ぶと、その項目の文例が出ます。
        </div>
      </div>
      <v-spacer />
      <v-btn class="mr-2" variant="outlined" :loading="exporting" @click="exportCsv">
        <v-icon start>mdi-download</v-icon>
        CSVをダウンロード
      </v-btn>
      <v-btn variant="outlined" color="primary" @click="importDialog = true">
        <v-icon start>mdi-upload</v-icon>
        CSVを取り込む
      </v-btn>
    </div>

    <!-- 帳票種別とレイアウトは分けて選ぶ (転記ルール側と揃える) -->
    <v-row align="center" class="mt-2">
      <v-col cols="12" md="3">
        <v-select
          v-model="planType"
          :items="createPlanTypeItems"
          label="帳票種別"
          variant="outlined"
          density="compact"
          color="primary"
          hide-details
        ></v-select>
      </v-col>
      <v-col cols="12" md="3">
        <v-select
          v-model="templateId"
          :items="templateItems"
          label="レイアウト"
          variant="outlined"
          density="compact"
          color="primary"
          hide-details
          no-data-text="この帳票種別のレイアウトがありません"
        ></v-select>
      </v-col>
      <v-col cols="12" md="4">
        <v-text-field
          v-model="keyword"
          label="文例を検索"
          variant="outlined"
          density="compact"
          color="primary"
          hide-details
          clearable
          prepend-inner-icon="mdi-magnify"
          @keyup.enter="load"
          @click:clear="clearKeyword"
        ></v-text-field>
      </v-col>
    </v-row>

    <div v-if="loading" class="text-center py-8">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>

    <template v-else-if="templateId">
      <div v-if="groups.length === 0" class="text-caption text-grey py-6">
        このレイアウトには文例グループが設定された項目がありません。レイアウトのセル設定で「文例グループ」を設定してください。
      </div>

      <v-card
        v-for="group in groups"
        :key="group.phraseKey"
        variant="outlined"
        class="mt-3 pa-3"
        :class="{ 'tp-phrase-group--focused': group.phraseKey === initialPhraseKey }"
      >
        <div class="d-flex align-center">
          <div class="text-subtitle-2">{{ group.phraseKey }}</div>
          <v-chip
            v-if="!group.existsInLayout"
            class="ml-2"
            size="x-small"
            variant="flat"
            color="grey"
          >
            現在のレイアウトに無い項目
          </v-chip>
          <v-spacer />
          <v-btn
            size="small"
            variant="text"
            color="primary"
            :disabled="!group.existsInLayout"
            @click="openCreateDialog(group.phraseKey)"
          >
            <v-icon start>mdi-plus</v-icon>
            文例を追加
          </v-btn>
        </div>

        <div v-if="group.phrases.length === 0" class="text-caption text-grey mt-1">
          文例がありません。
        </div>

        <div
          v-for="(phrase, index) in group.phrases"
          :key="phrase.phraseId"
          class="tp-phrase mt-2"
        >
          <div class="tp-phrase__body">{{ phrase.body }}</div>
          <div class="tp-phrase__actions">
            <v-btn
              size="x-small"
              variant="text"
              icon="mdi-arrow-up"
              :disabled="index === 0 || movingPhraseId !== null || isFiltering"
              :title="isFiltering ? '絞り込み中は並び替えできません' : '上へ'"
              @click="move(group, index, -1)"
            ></v-btn>
            <v-btn
              size="x-small"
              variant="text"
              icon="mdi-arrow-down"
              :disabled="index === group.phrases.length - 1 || movingPhraseId !== null || isFiltering"
              :title="isFiltering ? '絞り込み中は並び替えできません' : '下へ'"
              @click="move(group, index, 1)"
            ></v-btn>
            <v-btn size="x-small" variant="text" @click="openEditDialog(phrase)">編集</v-btn>
            <v-btn
              size="x-small"
              variant="text"
              color="error"
              :loading="deletingPhraseId === phrase.phraseId"
              @click="remove(phrase)"
            >
              削除
            </v-btn>
          </div>
        </div>
      </v-card>
    </template>

    <!-- 追加・編集モーダル -->
    <v-dialog v-model="editDialog" max-width="640px" persistent>
      <v-card>
        <v-card-title>{{ editingPhraseId ? "文例の編集" : "文例の追加" }}</v-card-title>
        <v-card-text>
          <div class="text-caption text-grey-darken-1 mb-2">項目: {{ editingPhraseKey }}</div>
          <v-textarea
            v-model="editingBody"
            rows="5"
            variant="outlined"
            density="compact"
            color="primary"
            :counter="phraseBodyMaxLength"
            placeholder="文例の本文を入力してください"
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="savingPhrase" @click="editDialog = false">
            キャンセル
          </v-btn>
          <v-btn
            color="primary"
            variant="flat"
            :loading="savingPhrase"
            :disabled="!isValidBody"
            @click="savePhrase"
          >
            保存
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- CSVインポートモーダル -->
    <v-dialog v-model="importDialog" max-width="640px" persistent>
      <v-card>
        <v-card-title>CSVを取り込む</v-card-title>
        <v-card-text>
          <div class="text-body-2 mb-3">
            「CSVをダウンロード」で取得したファイルに文例を書き足してアップロードしてください。
            <strong>既存の文例は消えず、追記されます。</strong>
          </div>
          <v-file-input
            v-model="importFile"
            label="CSVファイル"
            accept=".csv,text/csv"
            variant="outlined"
            density="compact"
            color="primary"
            hide-details
            prepend-icon=""
            prepend-inner-icon="mdi-paperclip"
          ></v-file-input>

          <v-alert v-if="importResult" type="info" variant="tonal" density="compact" class="mt-3">
            <div>{{ importResult.imported }}件を取り込みました。</div>
            <template v-if="importResult.skipped.length > 0">
              <div class="mt-2 font-weight-bold">
                取り込めなかった行（{{ importResult.skipped.length }}件）
              </div>
              <div class="text-caption">
                行番号はCSVのレコード番号です（Excelの行番号と一致します）。
              </div>
              <ul class="mt-1">
                <li v-for="skipped in importResult.skipped" :key="skipped.row">
                  {{ skipped.row }}行目: {{ skipped.reason }}
                </li>
              </ul>
            </template>
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="importing" @click="closeImportDialog">閉じる</v-btn>
          <v-btn
            color="primary"
            variant="flat"
            :loading="importing"
            :disabled="!importFile"
            @click="importCsv"
          >
            取り込む
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import {
  NurseryTeachingPlanPhraseGroup,
  NurseryTeachingPlanPhraseImportResponse,
  NurseryTeachingPlanPhraseItem,
  NurseryTeachingPlanTemplateListItem,
  NurseryTeachingPlanType,
} from "chaild-api/lib";
import ApiResource from "@/api/ApiResource";
import ApiTeachingPlan from "@/api/ApiTeachingPlan";
import { createPlanTypeItems, phraseBodyMaxLength } from "@/constants/teachingPlan";
import { confirmDanger, notifyError } from "@/utils/TeachingPlanDialog";

/**
 * 文例の管理 (api-design.md §5.8)。
 * 一覧はレイアウトの項目ごとにグルーピングされ、文例0件の項目も記入導線として表示される。
 */
export default defineComponent({
  name: "TeachingPlanPhraseTab",

  props: {
    nurseryId: {
      type: Number,
      required: true,
    },
    templates: {
      type: Array as PropType<NurseryTeachingPlanTemplateListItem[]>,
      default: () => [],
    },
    /** レイアウト編集から渡ってきたときの初期選択 */
    initialTemplateId: {
      type: [String, null] as PropType<string | null>,
      default: null,
    },
    /** 渡ってきた項目を強調する (どの項目から来たか見失わないように) */
    initialPhraseKey: {
      type: [String, null] as PropType<string | null>,
      default: null,
    },
  },

  mounted() {
    this.selectInitialTemplate();
  },

  data() {
    return {
      loading: false,
      loadToken: 0,
      planType: "overall" as NurseryTeachingPlanType,
      createPlanTypeItems,
      templateId: null as string | null,
      keyword: "",
      groups: [] as NurseryTeachingPlanPhraseGroup[],
      // 追加・編集
      editDialog: false,
      editingPhraseId: null as string | null,
      editingPhraseKey: "",
      editingBody: "",
      savingPhrase: false,
      deletingPhraseId: null as string | null,
      movingPhraseId: null as string | null,
      // CSV
      exporting: false,
      importDialog: false,
      importing: false,
      importFile: null as File | File[] | null,
      importResult: null as NurseryTeachingPlanPhraseImportResponse | null,
      phraseBodyMaxLength,
    };
  },

  computed: {
    /** キーワード絞り込み中か (並べ替えの可否判定に使う) */
    isFiltering(): boolean {
      return !!this.keyword;
    },
    /** 帳票種別が決まっているので名前だけで選べる */
    templateItems(): { title: string; value: string }[] {
      return this.templates
        .filter((template) => template.planType === this.planType)
        .map((template) => ({ title: template.name, value: template.templateId }));
    },
    isValidBody(): boolean {
      const trimmed = this.editingBody.trim();
      return trimmed.length > 0 && trimmed.length <= phraseBodyMaxLength;
    },
    selectedFile(): File | null {
      if (!this.importFile) {
        return null;
      }
      return Array.isArray(this.importFile) ? this.importFile[0] || null : this.importFile;
    },
  },

  watch: {
    templateId() {
      this.load();
    },
    /** 帳票種別を変えたら、その種別の先頭レイアウトへ寄せる */
    planType() {
      this.templateId = this.templateItems.length > 0 ? this.templateItems[0].value : null;
      if (this.templateId === null) {
        this.groups = [];
      }
    },
    templates() {
      if (this.templateId === null) {
        this.selectInitialTemplate();
      }
    },
  },

  methods: {
    /** 初期表示。レイアウト編集から渡された対象があればそれに合わせる */
    selectInitialTemplate() {
      if (this.initialTemplateId !== null) {
        const requested = this.templates.find(
          (template) => template.templateId === this.initialTemplateId
        );
        if (requested) {
          this.planType = requested.planType;
          this.templateId = requested.templateId;
          return;
        }
      }
      for (const item of createPlanTypeItems) {
        const first = this.templates.find((template) => template.planType === item.value);
        if (first) {
          this.planType = item.value;
          this.templateId = first.templateId;
          return;
        }
      }
      this.templateId = null;
    },
    clearKeyword() {
      this.keyword = "";
      this.load();
    },
    async load() {
      const templateId = this.templateId;
      if (!templateId) {
        this.groups = [];
        return;
      }
      this.loadToken += 1;
      const token = this.loadToken;
      this.loading = true;
      const response = await ApiTeachingPlan.listPhrases({
        nurseryId: this.nurseryId,
        templateId,
        keyword: this.keyword || undefined,
      }).catch(() => null);
      // レイアウト切り替え・検索の連打で応答が前後すると、別レイアウトの文例で
      // 表示が上書きされる。自分の応答だけを反映する
      if (token !== this.loadToken || this.templateId !== templateId) {
        return;
      }
      this.loading = false;
      this.groups = response ? response.data : [];
    },

    /**
     * 追加・編集・削除・並べ替え
     */
    openCreateDialog(phraseKey: string) {
      this.editingPhraseId = null;
      this.editingPhraseKey = phraseKey;
      this.editingBody = "";
      this.editDialog = true;
    },
    openEditDialog(phrase: NurseryTeachingPlanPhraseItem) {
      const group = this.groups.find((item) =>
        item.phrases.some((p) => p.phraseId === phrase.phraseId)
      );
      this.editingPhraseId = phrase.phraseId;
      this.editingPhraseKey = group ? group.phraseKey : "";
      this.editingBody = phrase.body;
      this.editDialog = true;
    },
    async savePhrase() {
      if (!this.templateId || !this.isValidBody) {
        return;
      }
      this.savingPhrase = true;
      const response = this.editingPhraseId
        ? await ApiTeachingPlan.updatePhrase({
            phraseId: this.editingPhraseId,
            body: this.editingBody.trim(),
          }).catch(() => null)
        : await ApiTeachingPlan.createPhrase({
            nurseryId: this.nurseryId,
            templateId: this.templateId,
            phraseKey: this.editingPhraseKey,
            body: this.editingBody.trim(),
          }).catch(() => null);
      this.savingPhrase = false;
      if (response) {
        this.editDialog = false;
        // 応答は絞り込み前の全量なので、検索条件を保つため引き直す
        await this.load();
      }
    },
    async remove(phrase: NurseryTeachingPlanPhraseItem) {
      if (
        !(await confirmDanger(
          "この文例を削除しますか？",
          `「${phrase.body.slice(0, 40)}${phrase.body.length > 40 ? "…" : ""}」を削除します。`
        ))
      ) {
        return;
      }
      this.deletingPhraseId = phrase.phraseId;
      const response = await ApiTeachingPlan.deletePhrase({
        phraseId: phrase.phraseId,
      }).catch(() => null);
      this.deletingPhraseId = null;
      if (response) {
        await this.load();
      }
    },
    /**
     * 画面上の並びを入れ替える (一覧は phraseKey → sortOrder → id 順で返る)。
     * sortOrderが同値だと並びが確定しないため、入れ替え後にグループ全体へ連番を振り直す。
     */
    async move(group: NurseryTeachingPlanPhraseGroup, index: number, direction: number) {
      // 絞り込み中は表示中のサブセットだけに連番を振り直してしまい、
      // 非表示の文例と sortOrder が衝突して全体の並びが壊れるため受け付けない
      if (this.isFiltering) {
        return;
      }
      const target = index + direction;
      if (target < 0 || target >= group.phrases.length) {
        return;
      }
      const moving = group.phrases[index];
      this.movingPhraseId = moving.phraseId;
      const reordered = [...group.phrases];
      reordered.splice(index, 1);
      reordered.splice(target, 0, moving);
      let failed = false;
      for (let i = 0; i < reordered.length; i += 1) {
        const phrase = reordered[i];
        if (phrase.sortOrder === i) {
          continue;
        }
        const updated = await ApiTeachingPlan.updatePhrase({
          phraseId: phrase.phraseId,
          sortOrder: i,
        }).catch(() => null);
        if (!updated) {
          failed = true;
        }
      }
      this.movingPhraseId = null;
      // 途中で失敗すると一部だけ順序が変わった状態になる。黙って完了扱いにせず、
      // サーバの実際の並びを読み直したことを伝える
      if (failed) {
        notifyError(
          "文例の並び替えを保存できませんでした",
          "通信状況を確認してください。最新の並び順を読み込み直します。"
        );
      }
      await this.load();
    },

    /**
     * CSV
     */
    async exportCsv() {
      if (!this.templateId) {
        return;
      }
      this.exporting = true;
      const response = await ApiTeachingPlan.exportPhrases({
        nurseryId: this.nurseryId,
        templateId: this.templateId,
      }).catch(() => null);
      this.exporting = false;
      if (response && response.url) {
        window.open(response.url, "_blank");
      }
    },
    closeImportDialog() {
      this.importDialog = false;
      this.importFile = null;
      this.importResult = null;
    },
    async importCsv() {
      const file = this.selectedFile;
      // 処理中にレイアウトを切り替えられても、開始時点のレイアウトへ取り込む
      // (this.templateId を読み直すと、別レイアウト用のCSVを新しいレイアウトへ流し込んでしまう)
      const templateId = this.templateId;
      if (!templateId || !file) {
        return;
      }
      this.importing = true;
      this.importResult = null;
      const uploadTarget = await ApiTeachingPlan.createPhraseImportUrl({
        nurseryId: this.nurseryId,
        templateId,
      }).catch(() => null);
      if (!uploadTarget) {
        this.importing = false;
        return;
      }
      const uploaded = await ApiResource.uploadFile({
        url: uploadTarget.preSignedUploadUrl,
        file,
      }).catch(() => null);
      if (!uploaded) {
        this.importing = false;
        notifyError("ファイルのアップロードに失敗しました", "通信状況を確認してからもう一度お試しください。");
        return;
      }
      const response = await ApiTeachingPlan.importPhrases({
        nurseryId: this.nurseryId,
        templateId,
        fileKey: uploadTarget.fileKey,
      }).catch(() => null);
      this.importing = false;
      if (response && this.templateId === templateId) {
        this.importResult = response;
        this.importFile = null;
        await this.load();
      }
    },
  },
});
</script>

<style scoped>
/* レイアウト編集から飛んできた項目を見失わないようにする */
.tp-phrase-group--focused {
  border-color: rgb(var(--v-theme-primary));
  border-width: 2px;
}
.tp-phrase {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  border: 1px solid #eceff1;
  border-radius: 4px;
  padding: 8px;
}
.tp-phrase__body {
  flex: 1;
  font-size: 13px;
  white-space: pre-wrap;
  word-break: break-word;
}
.tp-phrase__actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}
</style>
